import React from 'react';
import PropTypes from 'prop-types';
import useCards from '../Cards/useCards';
import { useDispatch, useSelector } from 'react-redux';
import { addToBench, attach, evolve, setActiveBenchPosition } from '../Pokemon/actions';
import { selectAttaching, selectBenching, selectEvolving } from '../Pokemon/selectors';
import { selectBench, selectGameIsStarted, selectIsActivePlayer, selectPlayer } from '../ActiveGame/selectors';
import { retreat } from '../RetreatButton/actions';
import { selectRetreating } from '../RetreatButton/selectors';
import { PlainButton } from '../Controls';
import CardFace from '../Card/CardFace';
import { Slot } from '../Card/styled';
import { BENCH } from '../ActiveGame/contextMenuTypes';
import { setContextMenu } from '../ActiveGame/actions';
import usePickTarget from '../Picks/usePickTarget';
import DropTarget from '../Drag/DropTarget';
import { useDragging } from '../Drag/dropTargets';
import usePokemonDrags from '../Board/usePokemonDrags';
import DraggablePokemon from '../Board/DraggablePokemon';
import { ACTIVE_TO_BENCH, BENCHED_TO_ACTIVE } from '../Board/pokemonDrags';
import { anchors } from '../Motion/anchorNames';
import { ATTACH_TARGET, BENCH_SPOT, EVOLVE_TARGET, selectBoardChoice, selectEvolveTargets, SWITCH_IN } from '../Board/selectors';

/** One of the five bench spots, at `position` 0–4. */
const BenchSlot = ({ userId, position }) => {
  const dispatch = useDispatch();
  const attaching = useSelector(selectAttaching);
  const bench = useSelector((state) => selectBench(state, { userId }));
  const benching = useSelector(selectBenching);
  const boardChoice = useSelector(selectBoardChoice);
  const gameIsStarted = useSelector(selectGameIsStarted);
  const isActivePlayer = useSelector((state) => selectIsActivePlayer(state, { userId }));
  const player = useSelector(selectPlayer);
  const retreating = useSelector(selectRetreating);
  const evolveTargets = useSelector(selectEvolveTargets);
  const evolving = useSelector(selectEvolving);

  const benchCard = bench && bench.get('cards').find(card => card.get('order') === position);
  const attachedTrainers = useCards(benchCard && benchCard.get('trainer_card_ids'));
  const pickTarget = usePickTarget(
    userId,
    position,
    benchCard ? benchCard.get('energyAttachments') : undefined,
  );
  const drags = usePokemonDrags(userId);
  const dragging = useDragging();
  // Choices only ever land on your own side: during your turn, or while both players set up.
  const isMine = userId === player.getIn(['user', 'id']);
  const choosingHere = isMine && (isActivePlayer || !gameIsStarted);

  if (!benchCard) {
    if (choosingHere && boardChoice === BENCH_SPOT) {
      const benchHere = () => dispatch(addToBench(position));
      return (
        <>
          <DropTarget name={anchors.bench(userId, position)} commit={benchHere} />
          <PlainButton onClick={benchHere} disabled={benching} aria-label="Put the Pokémon on your bench here">
            <Slot $targetable data-targetable="true">+</Slot>
          </PlainButton>
        </>
      );
    }

    return <Slot />;
  }

  const name = benchCard.get('name', 'this Pokémon');
  let onClick = () => {
    dispatch(setActiveBenchPosition(position));
    dispatch(setContextMenu(BENCH, userId));
  };
  // Whether a flow is picking this Pokémon: then a tap (or a drop) is the pick, with no question first; the turn
  // banner's Cancel backs out of the flow.
  let picking = false;
  let disabled = false;

  if (choosingHere && boardChoice === ATTACH_TARGET) {
    onClick = () => dispatch(attach(position));
    picking = true;
    disabled = attaching;
  }

  if (choosingHere && boardChoice === EVOLVE_TARGET && evolveTargets.includes(position)) {
    onClick = () => dispatch(evolve(position));
    picking = true;
    disabled = evolving;
  }

  // A Trainer or Power can pick either player's Pokémon; the hook knows which.
  if (pickTarget) {
    onClick = pickTarget.onClick;
    picking = true;
    disabled = pickTarget.busy;
  }

  if (choosingHere && boardChoice === SWITCH_IN) {
    onClick = () => dispatch(retreat(position));
    picking = true;
    disabled = retreating;
  }

  // Your active Pokémon dragged down here: a retreat to this one.
  const retreatHere = isMine && dragging && dragging.kind === ACTIVE_TO_BENCH ? () => drags.retreatTo(position) : null;

  return (
    <>
      {picking && !disabled && <DropTarget name={anchors.bench(userId, position)} commit={onClick} />}
      {retreatHere && <DropTarget name={anchors.bench(userId, position)} commit={retreatHere} />}
      <PlainButton onClick={onClick} disabled={disabled} aria-label={name}>
        <DraggablePokemon
          enabled={isMine && drags.up !== null && !disabled}
          place={anchors.bench(userId, position)}
          lift={() => ({ kind: BENCHED_TO_ACTIVE, benchPosition: position, how: drags.up })}
          settledBy={benchCard}
        >
          <CardFace
            card={benchCard}
            damage={benchCard.get('damage')}
            energyAttachments={benchCard.get('energyAttachments')}
            trainers={attachedTrainers}
            targetable={(picking && !disabled) || !!retreatHere}
          />
        </DraggablePokemon>
      </PlainButton>
    </>
  );
};

BenchSlot.propTypes = {
  userId: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
};

export default BenchSlot;
